import { getCurrentScope } from "@sentry/browser";

import { reverse } from "@reactivated";

import { config } from "../config";
import { getCaptchaToken } from "../forms/Captcha";
import { WebPageURL, isoWebPageURL } from "../models/nominals";
import {
    CSRAssistedUser,
    EmailOfferSignupResponse,
    ICSRAssistedUser,
    IEmailOfferSignupRequest,
    IEmailOfferSignupResponse,
    IOIDCTokenRefreshResponse,
    ISessionKeepAliveResponse,
    IUser,
    IUserLoginResponse,
    OIDCTokenRefreshResponse,
    SessionKeepAliveResponse,
    User,
    UserLoginResponse,
} from "../models/user";
import { check } from "../models/utils";
import { CSRF_HEADER, HttpError, fetchJson, getCSRFToken } from "../utils/ajax";
import { trackNewsletterSignup } from "../utils/analytics";
import { PromiseMutex } from "../utils/mutex";

export const load = async (): Promise<IUser> => {
    const mutex = new PromiseMutex<IUser>("load-current-user");
    let loading = mutex.getPromise();
    if (!loading) {
        loading = fetchJson(reverse("tsi-auth-current-user")).then(
            (body): IUser => {
                const user = check(User.decode(body));
                getCurrentScope().setUser(body as Record<string, unknown>);
                return user;
            },
        );
        mutex.setPromise(loading);
    }
    return loading;
};

export const getCurrentlyAssistedUser =
    async (): Promise<ICSRAssistedUser | null> => {
        const mutex = new PromiseMutex<ICSRAssistedUser | null>(
            "load-currently-assisted-user",
        );
        let loading = mutex.getPromise();
        if (!loading) {
            loading = fetchJson("/api/csr/users/currently_assisted_user/").then(
                (body): ICSRAssistedUser | null => {
                    if (!body) {
                        return null;
                    }
                    return check(CSRAssistedUser.decode(body));
                },
            );
            mutex.setPromise(loading);
        }
        return loading;
    };

export const updateAssistedUserEmail = async (
    userURL: string,
    email: string,
): Promise<ICSRAssistedUser> => {
    const body = await fetchJson(userURL, {
        method: "PATCH",
        headers: {
            "Content-Type": "application/json",
            [CSRF_HEADER]: await getCSRFToken(),
        },
        body: JSON.stringify({ email: email }),
    });
    return check(CSRAssistedUser.decode(body));
};

export const update = async (data: Partial<IUser>): Promise<IUser> => {
    const body = await fetchJson(reverse("tsi-auth-current-user"), {
        method: "PATCH",
        headers: {
            "Content-Type": "application/json",
            [CSRF_HEADER]: await getCSRFToken(),
        },
        body: JSON.stringify(data),
    });
    const user = check(User.decode(body));
    getCurrentScope().setUser(body as Record<string, unknown>);
    return user;
};

export const updateEmailAddress = async (email: string): Promise<IUser> => {
    return update({
        email: email,
    });
};

export const changePassword = async (newPassword: string): Promise<void> => {
    await fetchJson(reverse("tsi-auth-change-password"), {
        method: "PUT",
        headers: {
            "Content-Type": "application/json",
            [CSRF_HEADER]: await getCSRFToken(),
        },
        body: JSON.stringify({ password: newPassword }),
    });
};

export const login = async (
    email: string,
    password: string,
    nextURL?: WebPageURL,
): Promise<IUserLoginResponse> => {
    const body = await fetchJson(reverse("tsi-auth-login"), {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            [CSRF_HEADER]: await getCSRFToken(),
        },
        body: JSON.stringify({
            email: email,
            password: password,
            nextURL: nextURL ? isoWebPageURL.unwrap(nextURL) : undefined,
        }),
    });
    return check(UserLoginResponse.decode(body));
};

export const sendSessionKeepAlivePing = async (): Promise<
    ISessionKeepAliveResponse | IOIDCTokenRefreshResponse
> => {
    try {
        const params = new URLSearchParams({
            now: String(new Date().getTime()),
        });
        const body = await fetchJson(
            `${reverse("tsi-auth-keep-alive")}?${params}`,
            {
                headers: { "x-requested-with": "XMLHttpRequest" },
            },
        );
        return check(SessionKeepAliveResponse.decode(body));
    } catch (err) {
        if (err instanceof HttpError && err.body) {
            return check(OIDCTokenRefreshResponse.decode(err.body));
        }
        throw err;
    }
};

export const signupForEmailOffers = async (
    email: string,
    zipcode?: string,
    campaign_id?: string,
): Promise<IEmailOfferSignupResponse> => {
    const recaptcha = config.get("ENABLE_EMAIL_SIGNUP_CAPTCHA")
        ? await getCaptchaToken("email_signup")
        : null;
    const reqData: IEmailOfferSignupRequest = {
        email: email,
        zipcode: zipcode,
        campaign_id: campaign_id,
        recaptcha: recaptcha ?? undefined,
    };
    const body = await fetchJson(reverse("email-offers-signup"), {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            [CSRF_HEADER]: await getCSRFToken(),
        },
        body: JSON.stringify(reqData),
    });
    const result = check(EmailOfferSignupResponse.decode(body));
    void trackNewsletterSignup(email);
    return result;
};
